import classNames from 'classnames';
import { SearchType } from '@alltrails/types/Algolia';
import { AreaSearchResult, GuideMetadata, PlaceSearchResult } from '@alltrails/types/AlgoliaResults';
import { algoliaHitToResult } from '../../utils/algoliaResultUtils';
import styles from './styles/styles.module.scss';
import { Result } from './types';

type Args = {
  results: Result[];
  includeGuides: boolean;
  includeSponsoredLists: boolean;
  searchType: SearchType;
};

const reorderResults = ({ results, includeGuides, includeSponsoredLists, searchType }: Args) => {
  if (!results.length) {
    return results;
  }

  if (includeGuides && results[0].type === 'area') {
    // If the first result is an area with guides, remove guides for that area from the remaining list of results
    // and move them immediately below the area result
    const areaResult = results[0] as AreaSearchResult;
    const guides = areaResult.guides;
    if (!guides?.length) {
      return results;
    }

    const filteredResults = results.filter(result => !(result.type === 'guide' && result.area_id === areaResult.ID));
    if (guides.length === 1) {
      filteredResults.splice(1, 0, algoliaHitToResult({ ...guides[0], type: 'guide' }, searchType));
    } else if (guides.length > 1) {
      filteredResults.splice(1, 0, {
        type: 'guides',
        areaName: areaResult.short_name || areaResult.name,
        id: `${areaResult.ID}-guides`,
        guides: guides as GuideMetadata[],
        containerClassName: classNames(styles.carouselContainer, styles.guides),
        searchType
      });
    }
    return filteredResults;
  } else if (includeSponsoredLists && 'curated_lists' in results[0]) {
    // If the first result is place with curated lists, remove matching lists from the remaining list of results
    // and move them immediately below the place result
    const placeResult = results[0] as PlaceSearchResult;
    const curatedLists = placeResult.curated_lists;

    if (!curatedLists?.length) {
      return results;
    }

    const curatedListIds = curatedLists.map(list => list.ID);
    const filteredResults = results.filter(result => !(result.type === 'sponsored_list' && curatedListIds.includes(result.ID)));
    if (curatedLists.length === 1) {
      filteredResults.splice(1, 0, algoliaHitToResult(curatedLists[0], searchType));
    } else if (curatedLists.length > 1) {
      filteredResults.splice(1, 0, {
        type: 'sponsored-lists',
        lists: curatedLists,
        id: `${placeResult.ID}-sponsored-lists`,
        containerClassName: styles.carouselContainer,
        searchType
      });
    }
    return filteredResults;
  }

  return results;
};

export default reorderResults;
